import { Dispensary } from "./Dispensary";

export type CartChange = {
  item_name: string;
  error: string;
  message: string;
  metadata: {
    menu_item_id: number;
    variant_id: number;
    medical: boolean;
  };
};

export type CartItem = {
  dealTitle?: string | null;
  discountAmount?: number | null;
  discountedPackagePrice?: number | null;
  discountedPriceCents: number;
  discountType?: string | null;
  discountLabel: string | null;
  id: number;
  medical: boolean;
  menuItemData: CartMenuItem;
  menuItemId: number;
  packagePrice: number;
  packageSize: string;
  packageUnit: string;
  priceCents: number;
  quantity: number;
  savingsCents: number;
  variantId: number;
  variantExternalKey?: string;
};

export type CartTotalType = {
  deliveryFee: string | number | null;
  exciseTax: string | number | null;
  hasTax: boolean;
  otherTax: string | number | null;
  salesTax: string | number | null;
  savings: string | null;
  subtotal: string | number | null;
  tax: string | number | null;
  total: string | number | null;
  totalItems: number | null;
};

export type Cart = {
  dispensarySlug: string;
  id: number;
  bagHint: string | null;
  createdAt: string;
  userId: string | null;
  sessionId: string;
  reservationId: string | null;
  dispensaryId: number;
  subtotal: number;
  totalDiscountsCents: number;
  tax: number;
  taxTotals: { amountCents: number; category: string }[];
  total: number;
  totalWithTax: number;
  dispensaryData: Dispensary;
  cartItems: CartItem[];
  dealGroups: CartDealGroup[];
  selectedForDeliveryOption?: boolean;
  convertible: boolean;
  promoCodes?: PromoCodes[];
};

type PromoCodes = {
  cartId?: number;
  code?: string;
  id?: number;
  menuDealId?: number;
  qualified: boolean;
};

export type CartDealGroup = {
  deal: CartDeal | null;
  cartItems: CartItem[];
};

export type CartDeal = {
  id: number;
  kind: string | null;
  title: string | null;
  progress: number | null;
  progressText: string | null;
};

export type CartMenuItem = {
  brandId: number | null;
  brandName: string | null;
  category: string;
  cbdContent: number | null;
  cbdContentLabel: number | null;
  cbdUnit: string | null;
  created: string;
  description: string;
  dispensaryId: number;
  dispensarySlug: string;
  externalKey: string;
  hidden: boolean;
  id: string;
  imageUrl: string;
  isDeleted: boolean;
  isProductLinkable: boolean | null;
  isReservable: boolean;
  isStaffPick: boolean;
  lastModified: string;
  legacyMenuItemId: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  menuItemSettings: any;
  name: string;
  pickupOverride: boolean | null;
  productId: number;
  productImageUrl?: string;
  productLinkedAt: string | null;
  productLinkingRuleId: number | null;
  sortOrder: number;
  staffPickOrder: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  strain: any;
  strainId: number | null;
  strainKey: string | null;
  strainName: string | null;
  sysPeriod: string | null;
  thcContent: number | null;
  thcContentLabel: number | null;
  thcUnit: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  variants: any[];
};

// res-api sends a 200 with errors b/c of mobile
export type CartResult = {
  errors?: Array<{
    detail: string;
    title: string;
  }>;
  data?: Cart;
};

export type CartContextDeliveryAddress = {
  address1: string | null;
  address2: string | null;
  address_type: string | null;
  city: string | null;
  coordinates: {
    lat: number;
    lng: number;
  } | null;
  country_code: string | null;
  delivery_instructions: string | null;
  postal_code: string | null;
  county?: string | null;
  state: string | null;
};

export type CartContext = {
  deliveryAddress?: CartContextDeliveryAddress | null;
  fulfillmentMechanism?: string | null;
};

// This type used to be imported from API-OG but was extracted
// due to bundle sizing issues. Please consult API-OG code if you
// feel something may be missing.
export enum TaxType {
  CalculatedAtPayment = "calculated_at_payment",
  CanadianTaxTable = "canadian_tax_table",
  IncludedInMenu = "included_in_menu",
  MedOnly = "med_only",
  MedRec = "med_rec",
  PointOfSale = "point_of_sale",
  RecOnly = "rec_only",
}

// 12/14/23 This is a manually written type from the get /cart-items
// reservations api endpoint. This is loosely based on the response
// received and not a 1:1 mapping.
export type PreviouslyOrderedItem = {
  id: number | null;
  cart_id: number | null;
  menu_item_id: number;
  name: string | null;
  quantity: number | null;
  package_unit: string | null;
  package_size: string | null;
  package_price: number | null;
  created_at: string | null;
  updated_at: string | null;
  menu_item_data: {
    id: number | null;
    name: string | null;
    cbdPip: number | null;
    hidden: boolean | null;
    thcPip: number | null;
    brandId: number | null;
    cbdUnit: string | null;
    created: string | null;
    imageId: null; // unknown
    thcUnit: string | null;
    category: string | null;
    imageUrl: string | null;
    strainId: number | null;
    variants: {
      sku: string | null;
      pickup: boolean | null;
      batchId: null; // unknown
      medical: boolean | null;
      taxRate: number | null;
      lowStockAt: string | null;
      externalKey: string | null;
      packageSize: number | null;
      packageUnit: string | null;
      outOfStockAt: string | null;
      packagePrice: number | null;
      parentBatchId: null; // unknown
      stockQuantity: number | null;
      pickupOverride: boolean | null;
      priceIncludesTax: boolean | null;
      packageSizeAndUnit: string | null;
      unitPricingMeasure: string | null;
      packageWeightInGrams: null; // unknown
    }[];
    brandLogo: string | null;
    brandName: string | null;
    brandSlug: string | null;
    productId: number | null;
    sortOrder: number | null;
    strainKey: string | null;
    cbdContent: number | null;
    strainName: string | null;
    strainSlug: string | null;
    thcContent: number | null;
    description: string | null;
    externalKey: string | null;
    isStaffPick: boolean | null;
    brandCreated: string | null;
    dispensaryId: number | null;
    isReservable: boolean | null;
    lastModified: string | null;
    strainSymbol: string | null;
    strainCreated: string | null;
    dispensaryIcon: string | null;
    dispensaryName: string | null;
    dispensarySlug: string | null;
    dispensaryTags: string[] | null;
    pickupOverride: boolean | null;
    staffPickOrder: number | null;
    strainCategory: string | null;
    strainImageUrl: string | null;
    strainNugImage: string | null;
    strainSubtitle: string | null;
    dispensaryState: string | null;
    productImageUrl: string | null;
    dispensaryCountry: string | null;
    dispensaryCreated: string | null;
    dispensaryLogoUrl: string | null;
    dispensaryRatings: {
      reviewCount: number | null;
      averageRating: string | null;
      qualityRating: string | null;
      serviceRating: string | null;
      atmosphereRating: string | null;
      packagingQualityRating: number | null;
      easeOfRegistrationRating: number | null;
    };
    dispensaryTagLine: string | null;
    strainCollections: string[] | null;
    strainDescription: string | null;
    dispensaryLatitude: number | null;
    dispensarySchedule: {
      weekly: {
        [key: string]: {
          open: string | null;
          close: string | null;
          isOpen: boolean | null;
        };
      };
      openPeriods: {
        open: number | null;
        close: number | null;
      }[];
    };
    menuItemCategoryId: number | null;
    dispensaryLongitude: number | null;
    dispensaryIsApproved: boolean | null;
    dispensaryFeatureTier: string | null;
    strainShortDescription: string | null;
    dispensaryFollowerCount: number | null;
    dispensaryLastMenuUpdate: string | null;
    dispensaryHasDeliveryEnabled: boolean | null;
    dispensaryMapMarkerLocations: {
      city: string | null;
      label: string | null;
      state: string | null;
      country: string | null;
      latitude: number | null;
      longitude: number | null;
    }[];
    dispensaryInternalFeatureFlags: string[] | null;
    dispensaryHasReservationsEnabled: boolean | null;
    dispensaryLastOutdatedMenuNotification: string | null;
  };
  brand_name: string | null;
  category: string | null;
  custom: boolean | null;
  attribution_source: string | null;
  menu_deal_id: number | null;
  package_tax: number | null;
  medical: boolean | null;
  tax: number | null;
  deal_group_id: string | null;
  variant_external_key: string | null;
  brand_verified: boolean | null;
  variant_id: number | null;
  discount_type: string | null;
};
